"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { YouMightAlsoLikeItem } from "@/lib/blog/ymal-mapper";

interface YouMightAlsoLikeProps {
  items: YouMightAlsoLikeItem[];
  title?: string;
  noMargin?: boolean;
}

const TYPE_CONFIG = {
  blog:        { color: "var(--primary)"   },
  calculator:  { color: "var(--secondary)" },
  comparison:  { color: "var(--violet)"          },
  glossary:    { color: "var(--accent-orange)"          },
};

const ACTION_LABELS = {
  blog: "Read article",
  calculator: "Try it now",
  comparison: "Compare",
  glossary: "View term",
};

export default function YouMightAlsoLike({ items, title = "You Might Also Like", noMargin = false }: YouMightAlsoLikeProps) {
  // Minimum 3 required to render, maximum 10 shown
  if (!items || items.length < 3) return null;
  const visible = items.slice(0, 10);

  return (
    <section className="ymal-section" style={noMargin ? { marginTop: 0 } : undefined}>
      <div className="ymal-header">
        <h2 className="ymal-title">{title}</h2>
        <Link href="/blog" className="ymal-view-all">
          View All <ArrowRight size={14} />
        </Link>
      </div>
      <div className="ymal-carousel-wrapper">
        <div className="ymal-carousel">
          {visible.map((item, i) => {
            const config = TYPE_CONFIG[item.type];
            return (
              <div key={i} className="ymal-card-outer">
                <Link href={item.href} className="ymal-card">
                  {/* Image — shown for blog type */}
                  {item.image && (
                    <div className="ymal-image-wrap">
                      <img src={item.image.url} alt={item.image.alt} className="ymal-image" loading="lazy" />
                    </div>
                  )}
                  {/* Full title — never truncated */}
                  <h3 className="ymal-card-title">{item.title}</h3>
                  {/* Description — 2 line clamp to keep cards tidy */}
                  <p className="ymal-card-desc">{item.description}</p>
                  {/* CTA */}
                  <span className="ymal-action" style={{ color: config.color }}>
                    {ACTION_LABELS[item.type]}
                    <ArrowRight size={14} />
                  </span>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .ymal-section {
          margin-top: 3rem;
        }
        .ymal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        .ymal-title {
          font-size: var(--text-2xl);
          font-weight: var(--fw-bold);
          margin: 0;
          color: var(--text-primary);
        }
        .ymal-view-all {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: var(--text-sm);
          font-weight: var(--fw-bold);
          color: var(--primary);
          text-decoration: none;
          transition: color var(--transition-fast);
        }
        .ymal-view-all:hover {
          color: var(--primary-hover);
          text-decoration: underline;
        }
        .ymal-carousel-wrapper {
          position: relative;
          width: 100%;
        }
        /* Horizontal scrollable row */
        .ymal-carousel {
          display: flex;
          align-items: stretch;
          gap: var(--space-5);
          overflow-x: auto;
          overflow-y: visible;
          scroll-behavior: smooth;
          scrollbar-width: thin;
          scrollbar-color: var(--border-color-hover) transparent;
          /* Padding so hover shadows are not clipped */
          padding: var(--space-2) var(--space-1) var(--space-3);
          -webkit-overflow-scrolling: touch;
        }
        .ymal-carousel::-webkit-scrollbar {
          height: 4px;
        }
        .ymal-carousel::-webkit-scrollbar-track {
          background: transparent;
        }
        .ymal-carousel::-webkit-scrollbar-thumb {
          background-color: var(--border-color-hover);
          border-radius: var(--radius-full);
        }
        .ymal-carousel::-webkit-scrollbar-thumb:hover {
          background-color: var(--border-color);
        }

        /*
          Desktop: 3 cards fill full width.
          gap is var(--space-5) ≈ 1.5rem. 2 gaps across 3 cards = 3rem total.
          Each card = calc(33.333% - 1rem) accounts for the 2/3 share of total gap.
        */
        .ymal-card-outer {
          flex: 0 0 calc(33.333% - 1rem);
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        /* The actual card — fills height of outer */
        .ymal-card {
          display: flex;
          flex-direction: column;
          flex: 1;
          gap: var(--space-2);
          padding: var(--space-4);
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          text-decoration: none;
          color: inherit;
          transition: border-color 0.15s ease, transform 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 1px 3px var(--shadow-black-a04);
        }
        .ymal-card:hover {
          border-color: var(--border-color-hover);
          transform: translateY(-4px);
          box-shadow: 0 8px 25px var(--shadow-black-a08);
        }

        /* Image block */
        .ymal-image-wrap {
          width: 100%;
          aspect-ratio: 16/9;
          overflow: hidden;
          border-radius: var(--radius-sm);
          background: var(--bg-elevated);
          flex-shrink: 0;
          margin-bottom: var(--space-2);
        }
        .ymal-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Title — always fully shown, never truncated */
        .ymal-card-title {
          font-family: var(--font-heading);
          font-size: var(--text-sm);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin: 0;
          line-height: 1.3;
        }

        /* Description — 2 lines max so cards stay tidy */
        .ymal-card-desc {
          font-size: var(--text-xs);
          color: var(--text-secondary);
          line-height: 1.5;
          margin: 0;
          flex-grow: 1;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* CTA row */
        .ymal-action {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: var(--text-xs);
          font-weight: var(--fw-bold);
          margin-top: var(--space-1);
          flex-shrink: 0;
        }

        /* Tablet: 2 cards visible, 3rd peeks */
        @media (max-width: 900px) {
          .ymal-card-outer {
            flex: 0 0 calc(50% - 0.75rem);
          }
        }

        /* Mobile: fixed 260px cards — horizontal scroll reveals more */
        @media (max-width: 600px) {
          .ymal-card-outer {
            flex: 0 0 260px;
          }
        }
      `}</style>
    </section>
  );
}
